import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Github, Sparkles, ArrowUpRight, Folder, GitBranch, BarChart3 } from 'lucide-react';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const categories = ['all', 'AI/ML', 'Full-Stack'];

  const projects = [
    {
      id: 1,
      title: 'Reddit Insight Agents',
      subtitle: 'Multi-Agent Analysis System',
      description: 'Multi-agent workflow that automates Reddit thread sentiment analysis, fact-checking, and content insights.',
      tech: ['CrewAI', 'Streamlit', 'OpenAI GPT-4o', 'PRAW'],
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
      link: 'https://github.com/tushar80rt/RedditInsight',
      github: 'https://github.com/tushar80rt/RedditInsight',
      category: 'AI/ML',
      stats: {
        posts: '100+',
        savings: '60%',
        mode: 'Real-time'
      },
      outcome: 'Reduced manual thread analysis effort by 60% with real-time dashboard insights.',
      featured: true
    },
    {
      id: 2,
      title: 'Lost & Found 2.0',
      subtitle: 'Smart Matching Platform',
      description: 'Intelligent lost-item matching platform using semantic search, image similarity, and automated communication.',
      tech: ['CAMEL-AI', 'Streamlit', 'Twilio API', 'SQLite'],
      image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80',
      link: 'https://github.com/tushar80rt/lostandFound', 
      github: 'https://github.com/tushar80rt/lostandFound', 
      category: 'Full-Stack',
      stats: {
        channels: '3',
        alerts: 'Auto',
        matching: 'Real-time'
      },
      outcome: 'Enabled real-time matching with automated SMS, WhatsApp, and voice alerts.',
      featured: true
    }
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  const portfolioHighlights = [
    { label: 'Total Projects', value: `${projects.length}` },
    { label: 'Primary Domains', value: `${categories.length - 1}` },
    { label: 'Featured Builds', value: `${projects.filter((p) => p.featured).length}` }
  ];

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="relative py-20 px-6 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, var(--bg-primary) 0%, rgba(0, 217, 255, 0.02) 50%, var(--bg-primary) 100%)',
        minHeight: '90vh'
      }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/4 left-0 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0, 217, 255, 0.05) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'float 15s ease-in-out infinite'
          }}
        />
        <div
          className="absolute bottom-1/4 right-0 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255, 0, 255, 0.05) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'float 15s ease-in-out infinite reverse'
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-12 ${isVisible ? 'animate-fade-down' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 mb-4">
            <Folder size={20} style={{ color: 'var(--brand-primary)' }} />
            <span
              className="text-sm font-bold tracking-[0.2em] uppercase"
              style={{ color: 'var(--brand-primary)' }}
            >
              Portfolio
            </span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--text-primary) 0%, var(--brand-primary) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: '1.2'
            }}
          >
            Featured Projects
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--text-secondary)' }}
          >
            Building innovative AI solutions that solve real-world problems
          </p>
        </div>

        {/* Portfolio Highlights */}
        <div
          className={`grid grid-cols-3 gap-3 mb-8 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.15s' }}
        >
          {portfolioHighlights.map((item) => (
            <div
              key={item.label}
              className="rounded-xl px-3 py-3 text-center"
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <p className="text-lg font-bold" style={{ color: 'var(--brand-primary)' }}>
                {item.value}
              </p>
              <p className="text-xs md:text-sm" style={{ color: 'var(--text-muted)' }}>
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {/* Category Filter */}
        <div
          className={`flex justify-center gap-2 mb-10 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.2s' }}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300"
              style={{
                background: selectedCategory === category 
                  ? 'linear-gradient(135deg, var(--brand-primary), #00FF88)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: selectedCategory === category 
                  ? '1px solid transparent'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                color: selectedCategory === category 
                  ? '#000'
                  : 'var(--text-muted)',
                transform: selectedCategory === category ? 'scale(1.05)' : 'scale(1)'
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid - Compact */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className={`group relative ${isVisible ? 'animate-scale-in' : 'opacity-0'}`}
              style={{
                animationDelay: `${index * 0.1}s`
              }}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Featured Badge */}
              {project.featured && (
                <div
                  className="absolute -top-2 -right-2 z-10 px-2 py-1 rounded-full animate-pulse"
                  style={{
                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color: '#000',
                    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)'
                  }}
                >
                  <Sparkles size={10} className="inline mr-1" />
                  Featured
                </div>
              )}

              {/* Card */}
              <div
                className="relative h-full rounded-xl overflow-hidden transition-all duration-500"
                style={{
                  background: hoveredProject === project.id
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.02)',
                  border: `2px solid ${
                    hoveredProject === project.id 
                      ? 'var(--brand-primary)' 
                      : 'rgba(255, 255, 255, 0.1)'
                  }`,
                  backdropFilter: 'blur(10px)',
                  transform: hoveredProject === project.id 
                    ? 'translateY(-8px)' 
                    : 'translateY(0)',
                  boxShadow: hoveredProject === project.id
                    ? '0 20px 40px -10px rgba(0, 217, 255, 0.3)'
                    : '0 10px 30px -10px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Project Image - Smaller */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-all duration-700"
                    style={{
                      filter: `brightness(${hoveredProject === project.id ? 0.8 : 0.6})`,
                      transform: `scale(${hoveredProject === project.id ? 1.1 : 1})`
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(180deg, transparent 0%, rgba(10, 10, 20, 0.9) 100%)'
                    }}
                  />
                  
                  {/* Category Badge */}
                  <div
                    className="absolute top-3 left-3 px-2 py-1 rounded-md text-xs font-medium"
                    style={{
                      background: 'rgba(0, 217, 255, 0.2)',
                      border: '1px solid rgba(0, 217, 255, 0.5)',
                      color: 'var(--brand-primary)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    {project.category}
                  </div>

                  {/* Quick Stats Overlay */}
                  {hoveredProject === project.id && (
                    <div className="absolute bottom-3 left-3 right-3 flex justify-between animate-fade-up">
                      {Object.entries(project.stats).map(([key, value], idx) => (
                        <div
                          key={idx}
                          className="text-center"
                          style={{ animationDelay: `${idx * 0.05}s` }}
                        >
                          <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            {key}
                          </p>
                          <p className="text-sm font-bold" style={{ color: 'var(--brand-primary)' }}>
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Content - Compact */}
                <div className="p-5">
                  {/* Title & Subtitle */}
                  <div className="mb-3">
                    <h3
                      className="text-lg font-bold mb-1 flex items-center gap-2"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {project.title}
                      {hoveredProject === project.id && (
                        <ArrowUpRight 
                          size={16} 
                          className="animate-bounce"
                          style={{ color: 'var(--brand-primary)' }}
                        />
                      )}
                    </h3>
                    <p
                      className="text-xs font-medium"
                      style={{ color: 'var(--brand-primary)' }}
                    >
                      {project.subtitle}
                    </p>
                  </div>

                  {/* Description - Shorter */}
                  <p
                    className="text-sm mb-4 line-clamp-2"
                    style={{
                      color: 'var(--text-secondary)',
                      lineHeight: '1.5'
                    }}
                  >
                    {project.description}
                  </p>

                  <div
                    className="mb-4 rounded-lg p-2.5"
                    style={{
                      background: 'rgba(0, 217, 255, 0.08)',
                      border: '1px solid rgba(0, 217, 255, 0.2)'
                    }}
                  >
                    <p className="text-[11px] mb-1 flex items-center gap-1" style={{ color: 'var(--brand-primary)' }}>
                      <BarChart3 size={12} />
                      Outcome
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: '1.45' }}>
                      {project.outcome}
                    </p>
                  </div>

                  {/* Tech Stack - Compact */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {project.tech.slice(0, 3).map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-2 py-1 text-xs rounded-md transition-all duration-300"
                        style={{
                          background: hoveredProject === project.id 
                            ? 'rgba(0, 217, 255, 0.2)' 
                            : 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(0, 217, 255, 0.3)',
                          color: 'var(--brand-primary)'
                        }}
                      >
                        {tech}
                      </span>
                    ))}
                    {project.tech.length > 3 && (
                      <span
                        className="px-2 py-1 text-xs rounded-md"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          color: 'var(--text-muted)'
                        }}
                      >
                        +{project.tech.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Action Buttons - Compact */}
                  <div className="flex gap-2">
                    <a
                      href={project.link}
                      className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-medium transition-all duration-300 group/btn"
                      style={{
                        background: hoveredProject === project.id
                          ? 'linear-gradient(135deg, var(--brand-primary), #00FF88)'
                          : 'var(--brand-primary)',
                        color: '#000'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={14} className="group-hover/btn:rotate-12 transition-transform" />
                      Live Demo
                    </a>
                    <a
                      href={project.github}
                      className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm transition-all duration-300"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'var(--text-secondary)'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.borderColor = 'var(--brand-primary)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }}
                    >
                      <Github size={14} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Hover Glow Effect */}
              {hoveredProject === project.id && (
                <div
                  className="absolute inset-0 -z-10 rounded-xl opacity-50"
                  style={{
                    background: 'radial-gradient(circle at center, var(--brand-primary), transparent)',
                    filter: 'blur(30px)',
                    transform: 'scale(0.8)'
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* View All Projects Link */}
        <div
          className={`text-center mt-10 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.6s' }}
        >
          <a
            href="https://github.com/tushar80rt"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 group"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'var(--text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 217, 255, 0.1)';
              e.currentTarget.style.borderColor = 'var(--brand-primary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <GitBranch size={18} />
            <span>View All Projects on GitHub</span>
            <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-30px) translateX(10px); }
          50% { transform: translateY(20px) translateX(-10px); }
          75% { transform: translateY(-10px) translateX(15px); }
        }

        @keyframes animate-fade-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes animate-fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes animate-scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-down {
          animation: animate-fade-down 0.6s ease-out forwards;
        }

        .animate-fade-up {
          animation: animate-fade-up 0.6s ease-out forwards;
        }

        .animate-scale-in {
          animation: animate-scale-in 0.5s ease-out forwards;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default Projects;